import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, Users } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { StatusDot } from '@/components/ui/StatusDot'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { NumberInput } from '@/components/ui/NumberInput'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCrudList } from '@/hooks/useCrudList'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import {
  ModalitiesService,
  type Modality,
  type ModalityDegreeRelationship,
} from '@/services/modules/ModalitiesService'
import { DegreeRelationshipsService, type DegreeRelationship } from '@/services/modules/DegreeRelationshipsService'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { cn } from '@/lib/cn'
import { useAuthority } from '@/hooks/useAuthority'

export function SettingsModalitiesPage() {
  const [reload, setReload] = useState(0)
  // Rotas em /commercial/modalities — as chaves são do módulo comercial.
  const { can } = useAuthority()
  const canCreate = can('commercial.modalities.create')
  const canEdit = can('commercial.modalities.edit')
  const canDelete = can('commercial.modalities.delete')
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar modalidade' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(ModalitiesService, { size: 25, reloadKey: reload, filters: sf.filters })
  const [openForm, setOpenForm] = useState<{ open: boolean; item?: Modality }>({ open: false })
  const [openDelete, setOpenDelete] = useState<Modality | null>(null)

  const exp = useDataTableExport({
    filename: 'modalidades',
    service: ModalitiesService,
    filters: sf.filters,
    columns: [
      { header: 'Nome',                accessor: (m: Modality) => m.name },
      { header: 'Idade mínima',        accessor: (m: Modality) => m.minAge ?? 'Sem mínimo' },
      { header: 'Graus de parentesco', accessor: (m: Modality) => (m.degreeRelationships ?? []).map(d => d.degreeRelationship).join(', ') },
      { header: 'Status',              accessor: (m: Modality) => m.status },
    ],
  })

  async function handleSave() {
    setOpenForm({ open: false })
    setReload(v => v + 1)
  }
  async function handleDelete() {
    if (!openDelete) return
    try {
      await ModalitiesService.destroy(openDelete.id)
      toast.success({ title: 'Removida', description: 'Modalidade removida com sucesso.' })
      setOpenDelete(null)
      setReload(v => v + 1)
    } catch (e) {
      toast.error({ title: 'Erro ao remover', description: extractApiError(e).message })
    }
  }

  const columns: DataTableColumn<Modality>[] = [
    { key: 'name', header: 'Nome', sortable: true, alwaysVisible: true,
      render: m => <span className="font-medium text-gray-800">{m.name}</span> },
    { key: 'minAge', header: 'Idade mínima', align: 'center', className: 'w-32',
      render: m => m.minAge != null
        ? <span>{m.minAge} <span className="text-gray-400 text-[11px]">anos</span></span>
        : <span className="text-gray-400 italic">Sem mínimo</span> },
    { key: 'degrees', header: 'Graus de parentesco', align: 'center', className: 'w-48',
      render: m => <DegreesPopover degrees={m.degreeRelationships ?? []} /> },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24',
      render: m => (
        <StatusDot value={m.status} />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Modalidades"
        subtitle="Tipos de plano (Individual, Familiar, ) — idade mínima e graus de parentesco aceitos"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Comercial' }, { label: 'Modalidades' }]}
        actions={canCreate ? <Button onClick={() => setOpenForm({ open: true })} iconLeft={<Plus size={14} />}>Nova modalidade</Button> : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="settings.commercial.modalities"
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={m => m.id}
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        rowActions={(m) => [
          ...(canEdit ? [{ key: 'edit',   label: 'Editar',  icon: <Pencil size={14} />, onClick: () => setOpenForm({ open: true, item: m }) }] : []),
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />, danger: true, divider: canEdit, onClick: () => setOpenDelete(m) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ModalityForm
        open={openForm.open}
        item={openForm.item}
        onClose={() => setOpenForm({ open: false })}
        onSaved={handleSave}
      />
      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover modalidade?"
        description={`Tem certeza que deseja remover "${openDelete?.name}"? Planos que usam essa modalidade ficarão sem referência.`}
        confirmLabel="Remover"
        variant="danger"
      />
      {sf.drawer}
    </>
  )
}

function DegreesPopover({ degrees }: { degrees: ModalityDegreeRelationship[] }) {
  const count = degrees.length

  return (
    <InfoPopover
      ariaLabel="Ver graus permitidos"
      title={count === 0 ? 'Nenhum grau cadastrado' : `${count} ${count === 1 ? 'grau permitido' : 'graus permitidos'}`}
      align="center"
      minWidth={240}
      maxWidth={320}
      trigger={
        <span className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-medium',
          count === 0 ? 'bg-gray-100 text-gray-500' : 'bg-blue-50 text-blue-700 hover:bg-blue-100',
        )}>
          <Users size={12} />
          {count}
        </span>
      }
    >
      {count === 0 ? (
        <p className="text-gray-400 italic text-center py-2">Nenhum grau de parentesco cadastrado.</p>
      ) : (
        <ul className="space-y-1">
          {degrees.map(d => (
            <li key={d.id} className="flex items-center justify-between gap-2 text-[12px]">
              <span className="text-gray-700">{d.degreeRelationship}</span>
              {d.gender && (
                <span className="text-[10px] text-gray-500 uppercase tracking-wide">{d.gender}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </InfoPopover>
  )
}

function ModalityForm({ open, item, onClose, onSaved }: {
  open: boolean
  item?: Modality
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState('')
  const [minAge, setMinAge] = useState<number | null>(null)
  const [degreeIds, setDegreeIds] = useState<string[]>([])
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')
  const [saving, setSaving] = useState(false)
  const [degrees, setDegrees] = useState<DegreeRelationship[]>([])

  useEffect(() => {
    if (!open) return
    DegreeRelationshipsService.list({ size: 100, status: 'ATIVO' })
      .then(r => setDegrees(r.data))
      .catch(() => setDegrees([]))
    setName(item?.name ?? '')
    setMinAge(item?.minAge ?? null)
    setDegreeIds(item?.degreeRelationships?.map(d => d.id) ?? [])
    setStatus(item?.status ?? 'ATIVO')
  }, [open, item])

  function toggleDegree(id: string) {
    setDegreeIds(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id])
  }

  async function submit() {
    if (!name.trim()) {
      toast.error({ title: 'Verifique os dados', description: 'Informe o nome da modalidade.' })
      return
    }
    setSaving(true)
    try {
      const payload = { name: name.trim(), min_age: minAge, status, degree_relationship_ids: degreeIds }
      if (item) {
        await ModalitiesService.update(item.id, payload)
        toast.success({ title: 'Atualizada', description: 'Modalidade atualizada com sucesso.' })
      } else {
        await ModalitiesService.create(payload)
        toast.success({ title: 'Criada', description: 'Nova modalidade criada com sucesso.' })
      }
      onSaved()
    } catch (e) {
      toast.error({ title: 'Não foi possível salvar', description: extractApiError(e).message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={item ? 'Editar modalidade' : 'Nova modalidade'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} loading={saving}>Salvar</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Nome"
          required
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Ex.: Familiar +"
        />
        <NumberInput
          label="Idade mínima"
          value={minAge}
          onChange={setMinAge}
          min={0}
          max={150}
          placeholder="Sem mínimo"
          hint="Deixe em branco se não houver restrição de idade."
        />
        <div>
          <label className="text-xs font-medium text-gray-700 mb-1.5 block">
            Graus de parentesco aceitos
          </label>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 rounded-md ring-1 ring-gray-200 bg-gray-50/50">
            {degrees.length === 0 && (
              <span className="text-xs text-gray-400 italic">Nenhum grau de parentesco ativo cadastrado.</span>
            )}
            {degrees.map(d => {
              const active = degreeIds.includes(d.id)
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => toggleDegree(d.id)}
                  className={cn(
                    'cursor-pointer px-2.5 py-1 rounded-full text-[11px] font-medium ring-1 transition',
                    active
                      ? 'bg-brand-500 text-white ring-brand-500'
                      : 'bg-white text-gray-600 ring-gray-200 hover:bg-gray-100',
                  )}
                >
                  {d.degreeRelationship}
                </button>
              )
            })}
          </div>
          <p className="text-[11px] text-gray-500 mt-1.5">
            Selecione quais relações são permitidas em planos desta modalidade.
          </p>
        </div>
        <StatusToggle value={status} onChange={setStatus} />
      </div>
    </Modal>
  )
}
